// src/components/TOTPVerification.jsx
import React, { useState, useEffect, useRef } from 'react';
import { verifyTOTPToken } from '../utils/totpUtils';
import { useTranslation } from 'react-i18next';
import './TOTPVerification.css';

const TOTPVerification = ({ user, onVerificationSuccess, onCancel, onUseBackupCode }) => {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showBackupCode, setShowBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Focus on input when component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Start countdown timer
    startTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startTimer = () => {
    const now = new Date();
    const secondsLeft = 30 - (now.getSeconds() % 30);
    setTimeLeft(secondsLeft);

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setToken(value);
    setError('');

    // Auto-submit when 6 digits are entered
    if (value.length === 6) {
      handleVerification(value);
    }
  };

  const handleVerification = async (tokenValue = token) => {
    console.log('Verifying token:', tokenValue, 'with secret:', user.totpSecret);
    
    if (!tokenValue || tokenValue.length !== 6) {
      setError(t('totp.invalidToken'));
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const isValid = await verifyTOTPToken(tokenValue, user.totpSecret);
      console.log('Token verification result:', isValid);
      
      if (isValid) {
        console.log('Token is valid, calling onVerificationSuccess');
        onVerificationSuccess(tokenValue);
      } else {
        console.log('Token is invalid');
        setError(t('totp.invalidToken'));
        setToken('');
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    } catch (error) {
      console.error('Error verifying TOTP token:', error);
      setError(t('totp.verificationError'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackupCodeSubmit = async () => {
    if (!backupCode || backupCode.length !== 8) {
      setError(t('totp.invalidBackupCode'));
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // Check if backup code is valid (this would typically be done on the server)
      // For now, we'll assume the backup code is valid if it's 8 characters
      const isValidBackupCode = backupCode.length === 8;
      
      if (isValidBackupCode) {
        onUseBackupCode(backupCode);
      } else {
        setError(t('totp.invalidBackupCode'));
      }
    } catch (error) {
      console.error('Error verifying backup code:', error);
      setError(t('totp.backupCodeError'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showBackupCode) {
        handleBackupCodeSubmit();
      } else {
        handleVerification();
      }
    }
  };

  const formatTime = (seconds) => {
    return seconds.toString().padStart(2, '0');
  };

  return (
    <div className="totp-verification">
      <div className="verification-container">
        <h2>{t('totp.verificationTitle')}</h2>
        <p>{t('totp.verificationDescription')}</p>

        {!showBackupCode ? (
          <>
            <div className="timer-display">
              <div className="timer-circle">
                <span className="timer-text">{formatTime(timeLeft)}</span>
              </div>
              <p className="timer-label">{t('totp.timeRemaining')}</p>
            </div>

            <div className="token-input-container">
              <input
                ref={inputRef}
                type="text"
                value={token}
                onChange={handleTokenChange}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                maxLength="6"
                className="token-input"
                disabled={isVerifying}
              />
              {error && (
                <p className="error-message">{error}</p>
              )}
            </div>

            <div className="verification-actions">
              <button
                onClick={() => handleVerification()}
                disabled={isVerifying || token.length !== 6}
                className="verify-button"
              >
                {isVerifying ? t('totp.verifying') : t('totp.verify')}
              </button>
              
              <button
                onClick={() => setShowBackupCode(true)}
                className="backup-code-button"
              >
                {t('totp.useBackupCode')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="backup-code-container">
              <h3>{t('totp.backupCodeTitle')}</h3>
              <p>{t('totp.backupCodeDescription')}</p>
              
              <input
                type="text"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                onKeyPress={handleKeyPress}
                placeholder="XXXXXXXX"
                maxLength="8"
                className="backup-code-input"
                disabled={isVerifying}
              />
              
              {error && (
                <p className="error-message">{error}</p>
              )}
            </div>

            <div className="backup-code-actions">
              <button
                onClick={handleBackupCodeSubmit}
                disabled={isVerifying || backupCode.length !== 8}
                className="verify-button"
              >
                {isVerifying ? t('totp.verifying') : t('totp.verifyBackupCode')}
              </button>
              
              <button
                onClick={() => {
                  setShowBackupCode(false);
                  setBackupCode('');
                  setError('');
                }}
                className="back-button"
              >
                {t('totp.backToToken')}
              </button>
            </div>
          </>
        )}

        <div className="cancel-section">
          <button onClick={onCancel} className="cancel-button">
            {t('totp.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TOTPVerification;
